/**
 * Система расчета рейтинга для покерного клуба
 * (перенесено из backend/src/services/ratingCalculator.ts)
 */

export interface TournamentResult {
  tournamentId: number;
  userId: number;
  finalPosition: number;
  totalPlayers: number;
  tournamentType: string;
  entriesCount: number;
  knockoutsCount: number;
  guaranteePoints: number;
  hasBounty: boolean;
  bountyPoints: number;
  prizeDistribution?: number[];
}

export interface CalculatedPoints {
  positionPoints: number;
  koBonus: number;
  totalPoints: number;
  stageReached: string;
}

// Дефолтная призовая структура (абсолютные очки по местам)
const DEFAULT_PRIZE_DISTRIBUTION = [1750, 1100, 750, 450, 300, 225, 175, 125, 125];

function determineStage(position: number, totalPlayers: number): string {
  if (position === 1) return 'winner';
  if (position <= 3) return 'top_3';
  if (position <= 4) return 'top_4';
  if (totalPlayers >= 18 && position <= 9) return 'final_table';
  if (position <= 10) return 'top_10';
  if (totalPlayers >= 18 && position <= 18) return 'two_tables';
  return 'early';
}

function getPositionPoints(position: number, _guaranteePoints: number, totalPlayers: number, prizeDistribution?: number[]): number {
  const dist = prizeDistribution && prizeDistribution.length > 0 ? prizeDistribution : DEFAULT_PRIZE_DISTRIBUTION;
  const payoutPlaces = Math.min(totalPlayers, dist.length);
  if (position > payoutPlaces) return 0;
  return dist[position - 1] || 0;
}

export function calculateRatingPoints(result: TournamentResult): CalculatedPoints {
  const stage = determineStage(result.finalPosition, result.totalPlayers);
  const positionPoints = getPositionPoints(result.finalPosition, result.guaranteePoints, result.totalPlayers, result.prizeDistribution);
  const koBonus = result.hasBounty ? result.knockoutsCount * result.bountyPoints : 0;
  const totalPoints = positionPoints + koBonus;

  return {
    positionPoints,
    koBonus,
    totalPoints,
    stageReached: stage,
  };
}

export function updatePlayerRating(
  currentRating: number,
  pointsEarned: number,
): number {
  return Math.max(0, currentRating + pointsEarned);
}

export function calculateLevel(xp: number): number {
  return calculateLevelInfo(xp).level;
}

export interface LevelInfo {
  level: number;
  status: string;
  xp: number;
  xpInCurrentLevel: number;
  xpForNextLevel: number;
  progress: number;
  nextLevel: number;
  nextLevelXP: number;
}

const LEVEL_RANGES = [
  { min: 0, max: 1000, startLevel: 1, endLevel: 10, name: 'Новичок' },
  { min: 1000, max: 3000, startLevel: 11, endLevel: 20, name: 'Ученик' },
  { min: 3000, max: 6000, startLevel: 21, endLevel: 30, name: 'Игрок' },
  { min: 6000, max: 10000, startLevel: 31, endLevel: 40, name: 'Опытный' },
  { min: 10000, max: 15000, startLevel: 41, endLevel: 50, name: 'Мастер' },
  { min: 15000, max: 22000, startLevel: 51, endLevel: 60, name: 'Эксперт' },
  { min: 22000, max: 30000, startLevel: 61, endLevel: 70, name: 'Профи' },
  { min: 30000, max: 40000, startLevel: 71, endLevel: 80, name: 'Легенда' },
  { min: 40000, max: 50000, startLevel: 81, endLevel: 90, name: 'Икона' },
  { min: 50000, max: Infinity, startLevel: 91, endLevel: 100, name: 'Миф' },
];

export function calculateLevelInfo(xp: number): LevelInfo {
  for (const range of LEVEL_RANGES) {
    if (xp >= range.min && xp < range.max) {
      const levelRange = range.max - range.min;
      const levelsInRange = range.endLevel - range.startLevel + 1;
      const xpPerLevel = levelRange / levelsInRange;
      const xpInRange = xp - range.min;
      const levelProgress = xpInRange / xpPerLevel;
      const currentLevel = Math.max(range.startLevel, Math.floor(levelProgress) + range.startLevel);
      const xpInCurrentLevel = xpInRange % xpPerLevel;
      const xpForNextLevel = xpPerLevel - xpInCurrentLevel;
      const progress = (xpInCurrentLevel / xpPerLevel) * 100;
      const nextLevel = Math.min(currentLevel + 1, range.endLevel);
      const nextLevelXP = range.min + (Math.floor(levelProgress) + 1) * xpPerLevel;

      return {
        level: Math.min(Math.max(currentLevel, range.startLevel), range.endLevel),
        status: range.name,
        xp,
        xpInCurrentLevel,
        xpForNextLevel,
        progress,
        nextLevel,
        nextLevelXP,
      };
    }
  }

  return {
    level: 100,
    status: 'Миф',
    xp,
    xpInCurrentLevel: 0,
    xpForNextLevel: Infinity,
    progress: 100,
    nextLevel: 100,
    nextLevelXP: Infinity,
  };
}

export function calculateXP(
  finalPosition: number,
  _totalPlayers: number,
  knockoutsCount: number,
  currentStreak: number,
  hasBounty: boolean = false
): number {
  // XP for all positions

  let baseXP = 50;
  if (finalPosition === 1) baseXP = 500;
  else if (finalPosition <= 3) baseXP = 300;
  else if (finalPosition <= 9) baseXP = 200;
  else if (finalPosition <= 18) baseXP = 100;

  // Knockout XP only in bounty tournaments
  const koXP = hasBounty ? knockoutsCount * 20 : 0;

  let streakMultiplier = 1.0;
  if (currentStreak >= 5) streakMultiplier = 1.5;
  else if (currentStreak >= 3) streakMultiplier = 1.2;
  else if (currentStreak >= 2) streakMultiplier = 1.1;

  return Math.round(baseXP * streakMultiplier) + koXP;
}
